import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonIdentifiers.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonIdentifiers.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { PersonIdentifierViewModel, PersonIdentifierCreateCommand, PersonIdentifierUpdateCommand, PersonIdentifierVerificationFilter } from '../../models/Entities/Person/Identifier/PersonIdentifierModels';

export type KnownAction =
    | Actions.PersonIdentifiers.PersonIdentifierGetAllRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierGetAllSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierGetAllFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierGetByIdRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierGetByIdSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierGetByIdFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierCreateRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierCreateSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierCreateFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierUpdateRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierUpdateSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierUpdateFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierDeleteRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierDeleteSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierDeleteFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifyRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifySuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifyFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifyResetAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    GetIdentifiersByPersonId: (personId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETALL_REQUEST, personId });

        Services.GetIdentifiersByPersonId(token, personId)
            .then((response: PersonIdentifierViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_IDENTIFIER_GETALL_SUCCESS,
                    personId,
                    payload: response.map((item: PersonIdentifierViewModel) => ({ ...item, personId }))
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETALL_FAILURE, error });
            });
    },

    GetPersonIdentifierById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETBYID_REQUEST, id });

        Services.GetPersonIdentifierById(token, id)
            .then((response: PersonIdentifierViewModel) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_GETBYID_FAILURE, error });
            });
    },

    CreatePersonIdentifier: (payload: PersonIdentifierCreateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_CREATE_REQUEST, payload });

        Services.CreatePersonIdentifier(token, payload)
            .then((created: PersonIdentifierViewModel) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_CREATE_SUCCESS, payload: { ...created, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_CREATE_FAILURE, error });
            });
    },

    UpdatePersonIdentifier: (payload: PersonIdentifierUpdateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_UPDATE_REQUEST, payload });

        Services.UpdatePersonIdentifier(token, payload)
            .then((updated: PersonIdentifierViewModel) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_UPDATE_SUCCESS, payload: { ...updated, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_UPDATE_FAILURE, id: payload.id, error });
            });
    },

    DeletePersonIdentifier: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_DELETE_FAILURE, id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_DELETE_REQUEST, id });

        Services.DeletePersonIdentifier(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_DELETE_SUCCESS, id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_DELETE_FAILURE, id, error });
            });
    },

    VerifyIdentifier: (filter: PersonIdentifierVerificationFilter): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_VERIFY_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_VERIFY_REQUEST });

        Services.VerifyIdentifier(token, filter)
            .then((isAvailable: boolean) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_VERIFY_SUCCESS, payload: isAvailable });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_VERIFY_FAILURE, error });
            });
    },

    ResetIdentifierVerification: (): AppThunkAction<KnownAction> => async (dispatch) => {
        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_VERIFY_RESET });
    }
};
